import React, { useState } from "react";
import { parseLocalDate } from "utils/dateUtil";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip, LinearProgress, Box, Typography } from "@mui/material";
import { Edit, Delete, StatusDot as Circle } from "components/icons";

// Maps backend ProjectStatus enum values to MUI Chip/LinearProgress colors
const getStatusColor = (status) => {
  const colors = {
    // Backend ProjectStatus enum values
    PROPOSED: "secondary",
    APPROVED: "info",
    PLANNING: "info",
    IN_PROGRESS: "primary",
    ON_HOLD: "warning",
    COMPLETED: "success",
    CANCELLED: "error",
    // Legacy values for backwards compatibility
    ON_TRACK: "success",
    AT_RISK: "warning",
    CRITICAL: "error",
    NOT_STARTED: "secondary",
  };
  return colors[status] || "info"; // Use "info" as fallback (valid MUI color)
};

const getStatusIcon = (status) => {
  const colors = {
    // Backend ProjectStatus enum values
    PROPOSED: "#9e9e9e",
    APPROVED: "#2196f3",
    PLANNING: "#2196f3",
    IN_PROGRESS: "#1976d2",
    ON_HOLD: "#ff9800",
    COMPLETED: "#4caf50",
    CANCELLED: "#f44336",
    // Legacy values for backwards compatibility
    ON_TRACK: "#4caf50",
    AT_RISK: "#ff9800",
    CRITICAL: "#f44336",
    NOT_STARTED: "#757575",
  };
  return <Circle sx={{ color: colors[status] || "#2196f3", fontSize: 14 }} />;
};

export const ProjectDataGrid = ({ projects, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 70 },
    { field: "projectCode", headerName: "Code", width: 100 },
    { field: "projectName", headerName: "Project Name", width: 200, flex: 1 },
    { field: "clientName", headerName: "Client", width: 150 },
    { field: "projectManager", headerName: "PM", width: 130 },
    {
      field: "startDate",
      headerName: "Start",
      width: 110,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "endDate",
      headerName: "End",
      width: 110,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={getStatusIcon(params.value)}
          label={params.value?.replace("_", " ")}
          color={getStatusColor(params.value)}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "progress",
      headerName: "Progress",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Box sx={{ width: "100%", mr: 1 }}>
            <LinearProgress variant="determinate" value={params.value || 0} color={getStatusColor(params.row.status)} />
          </Box>
          <Typography variant="body2" color="text.secondary">{`${Math.round(params.value || 0)}%`}</Typography>
        </Box>
      ),
    },
    {
      field: "budget",
      headerName: "Budget",
      width: 100,
      valueFormatter: (value) => value ? `$${value.toLocaleString()}` : "N/A",
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={projects || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.projectId}
      />
    </MDBox>
  );
};

ProjectDataGrid.propTypes = {
  projects: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default ProjectDataGrid;

